import { z } from 'zod'
import { useI18n } from 'vue-i18n'
import type {
  PartnerRegistrationFormData,
  PartnerRegistrationSubmissionPayload,
} from '../../../types/partnerRegistration'
import { usePartnerRegistrationStore } from '../../stores/partnerRegistration.ts'
import { kftApiRequest } from '~/utils/kftApiRequest'

export const usePartnerRegistration = () => {
  const store = usePartnerRegistrationStore()
  const { t } = useI18n()

  const createValidationSchema = () => {
    return z
      .object({
        // Step 1 validations
        legal_name: z.string().min(1, t('validation.required')),
        phone: z.string().min(1, t('validation.required')),
        website: z.string().min(1, t('validation.required')),
        // Language can be either main_language or language (will be transformed)
        main_language: z.string().optional(),
        language: z.string().optional(),
        // Address validation - required fields
        street: z.string().min(1, t('validation.required')),
        country: z.string().min(1, t('validation.required')),
        locality: z.string().min(1, t('validation.required')),
        zipcode: z.string().min(1, t('validation.required')),
        street_number: z.string().optional(),
        // Optional fields
        business_size: z.string().optional(),
        annual_revenue: z.string().optional(),
        referral: z.string().optional(),

        // Step 2 validations
        experience_types: z.array(z.string()).min(1, t('validation.required')),
        booking_system_name: z.string().min(1, t('validation.required')),
        custom_boking_system_name: z.string().optional(),
        offer_type: z
          .string()
          .min(10, t('validation.min', { min: 10 }))
          .max(300, t('validation.max', { max: 300 })),

        // Step 3 validations
        user: z.object({
          first_name: z.string().min(1, t('validation.required')),
          last_name: z.string().min(1, t('validation.required')),
          email: z
            .string()
            .email(t('validation.email'))
            .min(1, t('validation.required')),
          password: z.string().min(8, t('validation.min', { min: 8 })),
        }),
        terms_accepted: z.boolean().refine((val) => val === true, {
          message: t('validation.shouldBeTrue'),
        }),
      })
      .refine(
        (data) => {
          // Either main_language or language must be present
          return !!(data.main_language || data.language)
        },
        {
          message: t('validation.required'),
          path: ['language'],
        }
      )
      .refine(
        (data) => {
          // If "Other third party provider link" is selected, custom_boking_system_name is required
          if (data.booking_system_name === 'Other third party provider link') {
            return (
              data.custom_boking_system_name &&
              data.custom_boking_system_name.trim().length > 0
            )
          }
          return true
        },
        {
          message: t('validation.required'),
          path: ['custom_boking_system_name'],
        }
      )
  }

  const validateFormData = (): {
    success: boolean
    errors?: z.ZodError
  } => {
    const schema = createValidationSchema()
    const result = schema.safeParse(store.formData)

    if (!result.success) {
      return { success: false, errors: result.error }
    }

    return { success: true }
  }

  const updateData = (data: Partial<PartnerRegistrationFormData>) => {
    store.updateData(data)
  }

  const getFormData = () => {
    return store.formData
  }

  const clearAllData = () => {
    store.clearFormData()
  }

  const prepareSubmissionPayload =
    (): PartnerRegistrationSubmissionPayload | null => {
      const {
        legal_name,
        phone,
        offer_type,
        user,
        main_language,
        language,
        specialization,
        experience_types,
        website,
        business_size,
        annual_revenue,
        referral,
        booking_system_name,
        custom_boking_system_name,
        country,
        admin_area,
        locality,
        zipcode,
        street,
        street_number,
        lat,
        lng,
        additional_information,
        address_components,
      } = store.formData

      // Validate required fields
      if (
        !legal_name ||
        !phone ||
        !offer_type ||
        !user?.first_name ||
        !user?.last_name ||
        !user?.email ||
        !user?.password
      ) {
        return null
      }

      // Transform language if needed
      const mainLanguage =
        main_language ||
        (language === 'de' ? 'de_DE' : language === 'en' ? 'en_US' : language)

      // Transform experience_types to specialization
      const finalSpecialization =
        specialization ||
        (experience_types?.length
          ? {
              experiences: 'EXPERIENCE',
              tours: 'TOUR',
              classes: 'COURSE',
              diy_kits: 'DIY_KIT',
            }[experience_types[0]] || 'EXPERIENCE'
          : undefined)

      if (!mainLanguage || !finalSpecialization) {
        return null
      }

      return {
        legal_name,
        phone,
        main_language: mainLanguage,
        specialization: finalSpecialization,
        offer_type,
        user: {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          password: user.password,
        },
        address: {
          country,
          admin_area,
          locality,
          zipcode,
          street,
          ...(street_number && { street_number }),
          lat,
          lng,
          address_components,
        },
        booking_system: booking_system_name,
        custom_booking_system: custom_boking_system_name,
        ...(website && { website }),
        ...(business_size && { business_size }),
        ...(annual_revenue && { annual_revenue }),
        ...(referral && { referral }),
        ...(additional_information !== undefined && {
          additional_information,
        }),
      }
    }

  const submitRegistration = async (): Promise<{
    success: boolean
    error?: string
  }> => {
    // Validate all form data before preparing payload
    const validation = validateFormData()
    if (!validation.success) {
      // Extract error messages from validation errors
      const errorMessages = validation.errors?.errors.map((err) => {
        const field = err.path.join('.')
        return `${field}: ${err.message}`
      })
      const errorMessage = errorMessages?.join('; ') || t('validation.required')
      return { success: false, error: errorMessage }
    }

    const payload = prepareSubmissionPayload()

    if (!payload) {
      return { success: false, error: t('general.error') }
    }

    try {
      const response = await kftApiRequest<
        PartnerRegistrationSubmissionPayload,
        unknown,
        { redirect_url: string }
      >('/v1/store/suppliers', {
        method: 'POST',
        body: payload,
      })

      sendPartnerOnboardingSubmit()

      // Clear form data and localStorage after successful submission
      clearAllData()

      // Redirect to the URL provided in the response
      if (response?.redirect_url) {
        window.location.href = response.redirect_url
      }

      return { success: true }
    } catch (error: any) {
      // Extract error message from various possible error structures
      let errorMessage = error?.data?.message || error?.message

      // Handle validation errors (errors object with field-specific messages)
      if (!errorMessage && error?.data?.errors) {
        const errorMessages = Object.entries(error.data.errors)
          .map(([_, messages]) => {
            if (Array.isArray(messages)) {
              return messages.join('; ')
            }
            return String(messages)
          })
          .join('; ')

        if (errorMessages) {
          errorMessage = errorMessages
        }
      }

      return {
        success: false,
        error: errorMessage || 'Failed to submit registration',
      }
    }
  }

  const goToNextStep = () => {
    const router = useRouter()
    const route = useRoute()
    const currentPath = route.path
    const stepMatch = currentPath.match(/\/partner\/register\/(\d+)/)

    if (stepMatch) {
      const currentStep = parseInt(stepMatch[1], 10)
      if (currentStep < 3) {
        router.push(`/partner/register/${currentStep + 1}`)
      }
    }
  }

  const goToPreviousStep = () => {
    const router = useRouter()
    const route = useRoute()
    const currentPath = route.path
    const stepMatch = currentPath.match(/\/partner\/register\/(\d+)/)

    if (stepMatch) {
      const currentStep = parseInt(stepMatch[1], 10)
      if (currentStep > 1) {
        router.push(`/partner/register/${currentStep - 1}`)
      }
    }
  }

  const goToStep = (step: number) => {
    if (step >= 1 && step <= 3) {
      const router = useRouter()
      router.push(`/partner/register/${step}`)
    }
  }

  return {
    formData: store.formData,
    updateData,
    getFormData,
    clearAllData,
    validateFormData,
    submitRegistration,
    goToNextStep,
    goToPreviousStep,
    goToStep,
  }
}
